import { useState } from "react"
import { Link, useLocation } from "react-router-dom"
import {Wheat, BookOpen} from 'lucide-react'
import { useAuth } from "@/hooks/useAuth"
import { lumi } from "@/lib/lumi"

export default function Header() {
  const { isAuthenticated, user } = useAuth()
  const location = useLocation()
  const [signingOut, setSigningOut] = useState(false)

  const handleAuth = () => {
    lumi.auth.openPopup()
  }

  const handleSignOut = async () => {
    setSigningOut(true)
    try {
      await lumi.auth.signOut()
    } finally {
      setSigningOut(false)
    }
  }

  const navLinkClass = (path: string) =>
    `relative flex items-center gap-1.5 text-sm font-medium transition-colors px-3 py-2 rounded-md ${
      location.pathname === path
        ? "text-indigo-700 bg-indigo-50"
        : "text-gray-600 hover:text-indigo-600 hover:bg-gray-50"
    }`

  return (
    <header className="no-print sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-100 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2.5 group">
            <div className="w-9 h-9 bg-gradient-to-br from-indigo-600 to-indigo-700 rounded-lg flex items-center justify-center flex-shrink-0 shadow-sm group-hover:shadow-md group-hover:scale-105 transition-all">
              <Wheat size={18} className="text-white" strokeWidth={2.2} />
            </div>
            <div className="flex flex-col leading-none">
              <span className="text-[15px] font-bold text-gray-900 group-hover:text-indigo-700 transition-colors tracking-tight">
                BakersCalculator
              </span>
              <span className="hidden sm:block text-[10px] font-medium text-gray-400 uppercase tracking-wider mt-0.5">
                Baker's Math Engine
              </span>
            </div>
          </Link>

          {/* Navigation */}
          <nav className="hidden sm:flex items-center gap-1">
            <Link to="/" className={navLinkClass("/")}>
              <Wheat size={14} strokeWidth={2.2} />
              Calculator Engine
            </Link>
            <Link to="/formulas" className={navLinkClass("/formulas")}>
              <BookOpen size={14} strokeWidth={2.2} />
              My Saved Formulas
            </Link>
          </nav>

          {/* Auth */}
          <div className="flex items-center gap-3">
            {isAuthenticated && user ? (
              <div className="flex items-center gap-3">
                <span className="hidden sm:flex items-center gap-2 text-sm text-gray-600 truncate max-w-32">
                  <span className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-700 text-[11px] font-bold flex items-center justify-center flex-shrink-0 uppercase">
                    {user.email?.[0] || "U"}
                  </span>
                  <span className="truncate">{user.email}</span>
                </span>
                <button
                  onClick={handleSignOut}
                  disabled={signingOut}
                  className="text-sm font-medium text-gray-600 hover:text-red-600 transition-colors px-3 py-2 rounded-md hover:bg-red-50 disabled:opacity-50"
                >
                  {signingOut ? "Signing out..." : "Sign Out"}
                </button>
              </div>
            ) : (
              <button
                onClick={handleAuth}
                className="text-sm font-semibold bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 active:scale-[0.97] transition-all shadow-sm hover:shadow"
              >
                Login / Signup
              </button>
            )}
          </div>
        </div>

        {/* Mobile Nav */}
        <div className="sm:hidden flex gap-1 pb-2">
          <Link to="/" className={navLinkClass("/")}>
            <Wheat size={14} strokeWidth={2.2} />
            Calculator
          </Link>
          <Link to="/formulas" className={navLinkClass("/formulas")}>
            <BookOpen size={14} strokeWidth={2.2} />
            My Formulas
          </Link>
        </div>
      </div>
      <div className="h-[2px] bg-gradient-to-r from-indigo-600 via-indigo-400 to-indigo-600 opacity-70"></div>
    </header>
  )
}
